import { Component } from '@theme/component';

/**
 * <caldera-gift-card-recipient>
 *
 * Legacy-style gift-card delivery form for the Horizon buy box, mirroring the
 * live Caldera gift card PDP: a "Send to myself" toggle (off by default, so the
 * recipient form shows first), recipient name / email / gift message / From
 * fields, and a SEND NOW / SEND LATER pair that reveals a scheduled-send date.
 *
 * The visible fields live outside Horizon's product <form> but are associated
 * with it via the HTML `form` attribute, so `new FormData(form)` picks them up
 * and `form.checkValidity()` gates the add-to-cart button natively — the same
 * mechanism the stock Horizon recipient form relies on, no ATC coupling.
 *
 * - Send to recipient (default) → fields enabled + required, control flag +
 *   timezone offset submitted so Shopify emails the recipient.
 * - Send to myself → fields disabled (excluded from submit + validation),
 *   control flag / timezone offset withheld.
 * - Send now (default) → date hidden, not required.
 * - Send later → date shown, required, constrained to a future window.
 *
 * @typedef {object} CalderaGiftCardRecipientRefs
 * @property {HTMLInputElement} toggle - "Send to myself" switch.
 * @property {HTMLElement} fields - Recipient fields container (collapses when off).
 * @property {HTMLInputElement} recipientName
 * @property {HTMLInputElement} recipientEmail
 * @property {HTMLTextAreaElement} recipientMessage
 * @property {HTMLInputElement} senderName
 * @property {HTMLButtonElement} sendNowButton
 * @property {HTMLButtonElement} sendLaterButton
 * @property {HTMLElement} sendOnField - Date-picker wrapper (hidden until "send later").
 * @property {HTMLInputElement} sendOn - Scheduled-send date input.
 * @property {HTMLInputElement} controlFlag - Shopify recipient control flag.
 * @property {HTMLInputElement} [timezoneOffset] - Shopify timezone offset.
 *
 * @extends {Component<CalderaGiftCardRecipientRefs>}
 */
class CalderaGiftCardRecipient extends Component {
  requiredRefs = [
    'toggle',
    'fields',
    'recipientName',
    'recipientEmail',
    'recipientMessage',
    'senderName',
    'sendNowButton',
    'sendLaterButton',
    'sendOnField',
    'sendOn',
    'controlFlag',
  ];

  /** How far ahead a gift card may be scheduled. */
  static MAX_SEND_DAYS = 90;

  connectedCallback() {
    super.connectedCallback();
    this.#setDateConstraints();
    // Markup ships in recipient + send-now state; sync attributes to match.
    this.#applyDeliveryMode();
    this.#applySendTiming();
  }

  /**
   * "Send to myself" switch.
   * @param {Event} _event
   */
  toggleDelivery(_event) {
    this.#applyDeliveryMode();
  }

  /** Choose immediate delivery. */
  sendNow() {
    this.#setSendTiming(false);
  }

  /** Choose scheduled delivery. */
  sendLater() {
    this.#setSendTiming(true);
  }

  /** @returns {boolean} true when the gift is addressed to someone else. */
  get #toRecipient() {
    return !this.refs.toggle.checked;
  }

  #applyDeliveryMode() {
    const toRecipient = this.#toRecipient;
    const { fields, recipientName, recipientEmail, recipientMessage, senderName, sendOn, controlFlag, timezoneOffset } =
      this.refs;

    fields.hidden = !toRecipient;

    // Required only when sending to a recipient; disabled fields are excluded
    // from FormData and from form.checkValidity(), so "send to myself" submits
    // straight away.
    for (const field of [recipientName, recipientEmail, senderName]) {
      field.disabled = !toRecipient;
      field.required = toRecipient;
    }
    recipientMessage.disabled = !toRecipient;
    sendOn.disabled = !toRecipient || !this.#sendingLater;

    controlFlag.disabled = !toRecipient;

    if (timezoneOffset) {
      timezoneOffset.disabled = !toRecipient;
      timezoneOffset.value = toRecipient ? String(new Date().getTimezoneOffset()) : '';
    }
  }

  /** @returns {boolean} */
  get #sendingLater() {
    return this.refs.sendLaterButton.getAttribute('aria-pressed') === 'true';
  }

  /** @param {boolean} later */
  #setSendTiming(later) {
    const { sendNowButton, sendLaterButton } = this.refs;
    sendNowButton.setAttribute('aria-pressed', String(!later));
    sendLaterButton.setAttribute('aria-pressed', String(later));
    sendNowButton.classList.toggle('is-active', !later);
    sendLaterButton.classList.toggle('is-active', later);
    this.#applySendTiming();
  }

  #applySendTiming() {
    const { sendOnField, sendOn } = this.refs;
    const later = this.#sendingLater;

    sendOnField.hidden = !later;
    sendOn.required = later && this.#toRecipient;
    sendOn.disabled = !later || !this.#toRecipient;
    if (!later) sendOn.value = '';
  }

  /** Constrain the date picker to a future window. */
  #setDateConstraints() {
    const format = (/** @type {Date} */ date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const max = new Date();
    max.setDate(today.getDate() + CalderaGiftCardRecipient.MAX_SEND_DAYS);

    this.refs.sendOn.setAttribute('min', format(today));
    this.refs.sendOn.setAttribute('max', format(max));
  }
}

if (!customElements.get('caldera-gift-card-recipient')) {
  customElements.define('caldera-gift-card-recipient', CalderaGiftCardRecipient);
}
